// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Selection Badge Component

import { SelectionBadge as SelectionBadgeType } from '@/types/selection';
import { 
  Tooltip, 
  TooltipContent, 
  TooltipProvider, 
  TooltipTrigger 
} from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// Icon imports from Lucide
import { 
  TrendingUp, 
  Snowflake, 
  Layout, 
  Wind, 
  Cloud, 
  Utensils, 
  GitBranch, 
  Award,
  Star
} from 'lucide-react';

// Map of badge IDs to icons
const BADGE_ICONS = {
  'top-producer': TrendingUp,
  'frost-monster': Snowflake,
  'structure-champion': Layout,
  'aroma-star': Wind,
  'smooth-smoke': Cloud,
  'flavor-profile': Utensils,
  'breeding-potential': GitBranch
};

interface SelectionBadgeProps {
  badge: SelectionBadgeType;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  showTooltip?: boolean;
}

export function SelectionBadge({ 
  badge, 
  size = 'md', 
  className = '',
  showTooltip = true
}: SelectionBadgeProps) {
  // Get the icon component for this badge
  const IconComponent = BADGE_ICONS[badge.id as keyof typeof BADGE_ICONS] || Award;
  
  // Size mappings
  const sizeMap = {
    sm: {
      badge: 'h-6 px-2 text-xs',
      icon: 'h-3 w-3 mr-1'
    },
    md: {
      badge: 'h-8 px-3 text-sm',
      icon: 'h-4 w-4 mr-1.5'
    },
    lg: {
      badge: 'h-10 px-4 text-base',
      icon: 'h-5 w-5 mr-2'
    }
  };
  
  const badgeStyle = {
    backgroundColor: badge.color,
    color: isLightColor(badge.color) ? '#1f2937' : 'white',
    borderColor: adjustColor(badge.color, -20)
  };
  
  const badgeContent = (
    <Badge 
      variant="outline" 
      className={cn(
        'flex items-center font-medium border-2', 
        sizeMap[size].badge, 
        className
      )}
      style={badgeStyle}
    >
      <IconComponent className={sizeMap[size].icon} />
      {badge.name}
    </Badge>
  );
  
  if (!showTooltip) {
    return badgeContent;
  }
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {badgeContent}
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm">{badge.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// Helper function to check if a color is light or dark
function isLightColor(hexColor: string): boolean {
  // Remove the # if it exists
  const hex = hexColor.replace('#', '');
  
  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  
  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  // Return true if light, false if dark
  return luminance > 0.5;
}

// Helper function to adjust a color's brightness
function adjustColor(hexColor: string, amount: number): string {
  // Remove the # if it exists
  let hex = hexColor.replace('#', '');
  
  // Convert to RGB
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  
  // Adjust each color
  r = Math.max(0, Math.min(255, r + amount));
  g = Math.max(0, Math.min(255, g + amount));
  b = Math.max(0, Math.min(255, b + amount));
  
  // Convert back to hex
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
} 